import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasFirstHand } from '../utils/dataBasis';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon, CheckIcon, ArrowTopRightOnSquareIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import API from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getViolationColor, getViolationTypeLabel } from '../types/caseForm';
import { formatCaseDate, formatIsoDate } from '../utils/formatDateValue';
import MarkdownText from './MarkdownText';
import Modal from './Modal';
import RichTextEditor from './RichTextEditor';
import { enumLabel } from '../utils/enumLabel';

/**
 * The compare view: a possible duplicate beside the case it may duplicate, with a decision at the
 * bottom. Shared by the merge page (/merge/:matchId) and the review-queue drawer.
 *
 * A merge only ADDS to the canonical, never removes. The reviewer settles, on this page, everything
 * a merge can carry over:
 *   - case fields that differ: keep the canonical's, take the duplicate's, or write one;
 *   - people: each victim and perpetrator on the duplicate is either already on the canonical
 *     (matched by name, with an override) or brought over;
 *   - violation-detail blocks: a block for a type the canonical lacks is brought over whole; a block
 *     both have is settled field by field (coded lists default to the union);
 *   - sources and documents: always attached.
 * Every choice is applied directly on confirm, recorded, and reversible by un-merge. No model is
 * involved; the basis line is facts about the pair and the engine line reports what the engine
 * said when it raised the match, or that a person raised it.
 */

export interface CompareMatch {
  id: string;
  similarityScore: number;
  llmConfidence: number;
  llmReasoning?: string;
  source?: 'PIPELINE' | 'GATE' | 'MANUAL';
  status?: string;
  case: { id: string; caseNumber: string };
  matchedCase: { id: string; caseNumber: string } | null;
}

export type DismissReason = 'NOT_RELATED' | 'SAME_SITUATION' | 'SAME_SUBJECT_DIFFERENT_EVENT';
export interface FieldOutcome { field: string; value: unknown }
export interface BlockFieldOutcome { violationType: string; field: string; value: unknown }
export interface EntityOutcomes { victimIds: string[]; perpetratorIds: string[]; violationDetailIds: string[]; developmentProjectIds: string[] }
export interface VictimFieldOutcome { victimId: string; violation?: string; field: string; value: unknown }
export interface DecideExtra { fieldOutcomes?: FieldOutcome[]; blockFieldOutcomes?: BlockFieldOutcome[]; victimFieldOutcomes?: VictimFieldOutcome[]; entityOutcomes?: EntityOutcomes; dismissReason?: DismissReason; acknowledgeRestriction?: boolean }

interface VictimViolationRow extends Record<string, unknown> { violationType: string; otherViolationType?: string | null; affectedCount?: number | null; affectedMaleCount?: number | null; affectedFemaleCount?: number | null; accusationOutcome?: string | null; detentionType?: string | null; detentionDays?: number | null; dueProcessViolations?: string[] }
interface VictimRow extends Record<string, unknown> {
  id: string; isGroup: boolean; firstName?: string; lastName?: string; groupName?: string[]; groupSize?: number; groupType?: string | null;
  gender?: string; ageBracket?: string | null; dateOfBirth?: string | null; indigenousGroupOfVictim?: string; isIndigenous?: boolean;
  communityRole?: string | null; communityRoleType?: string | null; dependentType?: string | null; dependentDetails?: string | null; additionalInfo?: string | null;
  affectedIndividualsCount?: number | null; affectedFamiliesCount?: number | null; affectedHomesCount?: number | null; affectedCommunitiesCount?: number | null; affectedVillagesCount?: number | null;
  violationDetails?: VictimViolationRow[];
}

// Victim fields worth showing in the expandable details, in display order. Names and dates of birth
// follow the masking rule; everything else is shown when filled.
const VICTIM_DETAIL_FIELDS: Array<{ field: string; label: string; kind: Kind }> = [
  { field: 'gender', label: 'Gender', kind: 'enum' },
  { field: 'ageBracket', label: 'Age bracket', kind: 'text' },
  { field: 'dateOfBirth', label: 'Date of birth', kind: 'date' },
  { field: 'indigenousGroupOfVictim', label: 'Indigenous group', kind: 'text' },
  { field: 'communityRoleType', label: 'Role type', kind: 'enum' },
  { field: 'communityRole', label: 'Role', kind: 'text' },
  { field: 'groupType', label: 'Group type', kind: 'enum' },
  { field: 'groupSize', label: 'Group size', kind: 'text' },
  { field: 'affectedIndividualsCount', label: 'People affected', kind: 'text' },
  { field: 'affectedFamiliesCount', label: 'Families affected', kind: 'text' },
  { field: 'affectedHomesCount', label: 'Homes affected', kind: 'text' },
  { field: 'affectedCommunitiesCount', label: 'Communities affected', kind: 'text' },
  { field: 'affectedVillagesCount', label: 'Villages affected', kind: 'text' },
  { field: 'dependentType', label: 'Dependents', kind: 'text' },
  { field: 'dependentDetails', label: 'Dependent details', kind: 'textarea' },
  { field: 'additionalInfo', label: 'Additional information', kind: 'textarea' },
];
interface PerpRow { id: string; name?: string; firstName?: string; lastName?: string; type?: string }
type ProjectRow = Record<string, unknown> & { id: string; name?: string | null; type?: string | null; subtypes?: string[]; description?: string | null; startYear?: number | null; source?: string | null; websiteLink?: string | null };
type BlockRow = Record<string, unknown> & { id: string; violationType: string };

export interface FullCase {
  id: string;
  caseNumber: string;
  status: string;
  description?: string;
  country?: string;
  region?: string;
  city?: string;
  specificLocation?: string;
  indigenousGroup?: string;
  indigenousTerritory?: string;
  caseStartDate?: string;
  caseStartDatePrecision?: 'DAY' | 'MONTH' | 'YEAR';
  caseEndDate?: string;
  isCaseOngoing?: boolean;
  incidentDate?: string;
  violationTypes?: string[];
  otherViolationTypes?: string[];
  violationDetails?: BlockRow[];
  victims?: VictimRow[];
  perpetrators?: PerpRow[];
  familyImpact?: string;
  communityImpact?: string;
  investigationDetails?: string;
  advocacyDetails?: string;
  sourceInformation?: string;
  metadata?: { additionalSources?: Array<{ url: string; platform?: string | null }> };
  isAutoSourced?: boolean;
  isPrimaryData?: boolean;
  dataBasis?: string;
  dataSource?: string;
  dataUsageRestriction?: string;
  createdAt?: string;
  developmentProjectName?: string;
  developmentProjects?: ProjectRow[];
  incidentTime?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  otherViolationTypeDetails?: string | null;
  impactTypes?: string[];
  tags?: string[];
  investigationConducted?: boolean | null;
  investigationWhyNot?: string | null;
  advocacyUndertaken?: boolean | null;
  advocacyWhyNot?: string | null;
  isRelatedToDevelopmentProject?: boolean | null;
}

type Kind = 'text' | 'textarea' | 'date' | 'boolean' | 'enum' | 'enumList' | 'list' | 'number';
type Pick = 'canonical' | 'duplicate' | 'custom' | 'union';
interface Choice { pick: Pick; custom: string }

// Scalar case fields the reviewer may settle. Mirrors MERGE_SCALAR_FIELDS in the backend's
// caseUpdatesController; the backend ignores anything else.
const SCALAR_FIELDS: Array<{ field: keyof FullCase & string; label: string; kind: Kind }> = [
  { field: 'description', label: 'Description', kind: 'textarea' },
  { field: 'incidentDate', label: 'Incident date', kind: 'date' },
  { field: 'caseStartDate', label: 'Case start', kind: 'date' },
  { field: 'caseEndDate', label: 'Case end', kind: 'date' },
  { field: 'region', label: 'Region', kind: 'text' },
  { field: 'city', label: 'City', kind: 'text' },
  { field: 'specificLocation', label: 'Specific location', kind: 'text' },
  { field: 'indigenousGroup', label: 'Indigenous group', kind: 'text' },
  { field: 'indigenousTerritory', label: 'Territory', kind: 'text' },
  { field: 'familyImpact', label: 'Family impact', kind: 'textarea' },
  { field: 'communityImpact', label: 'Community impact', kind: 'textarea' },
  { field: 'investigationDetails', label: 'Investigation', kind: 'textarea' },
  { field: 'advocacyDetails', label: 'Advocacy', kind: 'textarea' },
  { field: 'investigationConducted', label: 'Investigation conducted', kind: 'boolean' },
  { field: 'investigationWhyNot', label: 'Why no investigation', kind: 'textarea' },
  { field: 'advocacyUndertaken', label: 'Advocacy undertaken', kind: 'boolean' },
  { field: 'advocacyWhyNot', label: 'Why no advocacy', kind: 'textarea' },
  { field: 'incidentTime', label: 'Incident time', kind: 'text' },
  { field: 'caseStartDatePrecision', label: 'Start date precision', kind: 'enum' },
  { field: 'isCaseOngoing', label: 'Ongoing', kind: 'boolean' },
  { field: 'latitude', label: 'Latitude', kind: 'number' },
  { field: 'longitude', label: 'Longitude', kind: 'number' },
  { field: 'otherViolationTypes', label: 'Other violation types', kind: 'enumList' },
  { field: 'otherViolationTypeDetails', label: 'Other violation details', kind: 'textarea' },
  { field: 'impactTypes', label: 'Impact types', kind: 'enumList' },
  { field: 'tags', label: 'Tags', kind: 'list' },
  { field: 'isRelatedToDevelopmentProject', label: 'Related to a development project', kind: 'boolean' },
];

// Victim fields settled for a victim the canonical already has (matched by name). Mirrors
// MERGE_VICTIM_FIELDS / MERGE_VVD_FIELDS in the backend.
const VICTIM_MERGE_FIELDS: Array<{ field: string; label: string; kind: Kind }> = [
  { field: 'gender', label: 'Gender', kind: 'enum' },
  { field: 'ageBracket', label: 'Age bracket', kind: 'text' },
  { field: 'dateOfBirth', label: 'Date of birth', kind: 'date' },
  { field: 'indigenousGroupOfVictim', label: 'Indigenous group', kind: 'text' },
  { field: 'isIndigenous', label: 'Indigenous', kind: 'boolean' },
  { field: 'communityRoleType', label: 'Role type', kind: 'enum' },
  { field: 'communityRole', label: 'Role', kind: 'text' },
  { field: 'groupType', label: 'Group type', kind: 'enum' },
  { field: 'groupSize', label: 'Group size', kind: 'number' },
  { field: 'affectedIndividualsCount', label: 'People affected', kind: 'number' },
  { field: 'affectedFamiliesCount', label: 'Families affected', kind: 'number' },
  { field: 'affectedHomesCount', label: 'Homes affected', kind: 'number' },
  { field: 'affectedGroupsCount', label: 'Groups affected', kind: 'number' },
  { field: 'affectedCommunitiesCount', label: 'Communities affected', kind: 'number' },
  { field: 'affectedVillagesCount', label: 'Villages affected', kind: 'number' },
  { field: 'dependentType', label: 'Dependents', kind: 'text' },
  { field: 'dependentDetails', label: 'Dependent details', kind: 'textarea' },
  { field: 'additionalInfo', label: 'Additional information', kind: 'textarea' },
];
const VVD_MERGE_FIELDS: Array<{ field: string; label: string; kind: Kind }> = [
  { field: 'affectedCount', label: 'Affected', kind: 'number' },
  { field: 'affectedMaleCount', label: 'Male', kind: 'number' },
  { field: 'affectedFemaleCount', label: 'Female', kind: 'number' },
  { field: 'affected0to12Count', label: 'Aged 0–12', kind: 'number' },
  { field: 'affected13to25Count', label: 'Aged 13–25', kind: 'number' },
  { field: 'affected26to35Count', label: 'Aged 26–35', kind: 'number' },
  { field: 'affected36to59Count', label: 'Aged 36–59', kind: 'number' },
  { field: 'affected60PlusCount', label: 'Aged 60+', kind: 'number' },
  { field: 'accusationOutcome', label: 'Accusation outcome', kind: 'enum' },
  { field: 'detentionType', label: 'Detention', kind: 'enum' },
  { field: 'detentionDays', label: 'Days detained', kind: 'number' },
  { field: 'dueProcessViolations', label: 'Due-process violations', kind: 'enumList' },
];
// A victim's per-violation row key, as the backend's applier addresses it: KILLING / CRIMINALIZATION, or the other-violation code.
const vvdKey = (r: { violationType: string; otherViolationType?: string | null }) => (['KILLING', 'CRIMINALIZATION'].includes(r.violationType) ? r.violationType : (r.otherViolationType || 'OTHER'));

// Violation-detail block fields, for blocks both records carry. Mirrors MERGE_BLOCK_FIELDS.
const BLOCK_FIELDS: Array<{ field: string; label: string; kind: Kind }> = [
  { field: 'perpetratorKnown', label: 'Perpetrator known', kind: 'boolean' },
  { field: 'perpetratorTypes', label: 'Perpetrator types', kind: 'enumList' },
  { field: 'perpetratorDetails', label: 'Perpetrator details', kind: 'textarea' },
  { field: 'perpetratorAccountabilityStatus', label: 'Accountability status', kind: 'enum' },
  { field: 'legalCaseFiled', label: 'Legal case filed', kind: 'boolean' },
  { field: 'legalCaseDetails', label: 'Legal case details', kind: 'textarea' },
  { field: 'legalCaseWhyNot', label: 'Why no legal case', kind: 'textarea' },
  { field: 'stateActionUndertaken', label: 'State action undertaken', kind: 'boolean' },
  { field: 'stateActionDetails', label: 'State action details', kind: 'textarea' },
  { field: 'stateActionWhyNot', label: 'Why no state action', kind: 'textarea' },
  { field: 'criminalizationTypes', label: 'Criminalization types', kind: 'enumList' },
  { field: 'criminalizationTriggers', label: 'Criminalization triggers', kind: 'enumList' },
  { field: 'legalInstrumentTypes', label: 'Legal instruments', kind: 'enumList' },
  { field: 'proceedingType', label: 'Proceeding type', kind: 'enum' },
  { field: 'isRetaliatoryProceeding', label: 'Retaliatory proceeding', kind: 'boolean' },
  { field: 'whyVictimsCriminalized', label: 'Why criminalized', kind: 'textarea' },
  { field: 'harassmentPrior', label: 'Prior harassment', kind: 'boolean' },
  { field: 'harassmentPriorDetails', label: 'Prior harassment details', kind: 'textarea' },
  { field: 'accusations', label: 'Accusations', kind: 'textarea' },
  { field: 'policiesOrLawsUsed', label: 'Policies or laws used', kind: 'textarea' },
  { field: 'whoFiledAccusations', label: 'Who filed accusations', kind: 'enumList' },
  { field: 'whoFiledAccusationsDetails', label: 'Who filed, details', kind: 'textarea' },
  { field: 'accusationsLegallyFiled', label: 'Accusations legally filed', kind: 'boolean' },
  { field: 'courtFilingLocation', label: 'Court filing location', kind: 'text' },
  { field: 'courtFilingDetails', label: 'Court filing details', kind: 'textarea' },
  { field: 'decisionOnAccusations', label: 'Decision on accusations', kind: 'boolean' },
  { field: 'decisionDetails', label: 'Decision details', kind: 'textarea' },
  { field: 'victimsInPrison', label: 'Victims in prison', kind: 'boolean' },
  { field: 'prisonConditions', label: 'Prison conditions', kind: 'textarea' },
  { field: 'victimsSituationIfNotInPrison', label: 'Situation if not in prison', kind: 'textarea' },
  { field: 'whyVictimsKilled', label: 'Why killed', kind: 'textarea' },
  { field: 'howKillingCarriedOut', label: 'How the killing was carried out', kind: 'textarea' },
  { field: 'cause', label: 'Cause', kind: 'textarea' },
];

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft', FOR_VALIDATION: 'For Validation', IN_RESEARCH: 'In Research',
  FOR_APPROVAL: 'For Approval', APPROVED: 'Approved', REJECTED: 'Rejected', ARCHIVED: 'Archived',
};

const norm = (v?: string | null) => (v || '').trim().toLowerCase();
const normUrl = (u?: string | null) => (u || '').trim().replace(/^https?:\/\/(www\.)?/i, '').replace(/\/+$/, '').toLowerCase();
const victimName = (v: { firstName?: string; lastName?: string }) => [v.firstName, v.lastName].filter(Boolean).join(' ');
const groupKey = (v: { groupName?: string[] }) => (v.groupName || []).map(norm).sort().join('|');
const perpName = (p: { name?: string; firstName?: string; lastName?: string }) => p.name || [p.firstName, p.lastName].filter(Boolean).join(' ');
const isoDay = (v?: unknown) => (v ? String(v).slice(0, 10) : '');
const urlsOf = (c?: FullCase) => (c ? [c.sourceInformation, ...(c.metadata?.additionalSources || []).map((s) => s.url)].filter((u): u is string => !!u) : []);
const plural = (n: number, s: string) => `${n} ${s}${n === 1 ? '' : 's'}`;
const isEmptyVal = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
const differs = (av: unknown, bv: unknown, kind: Kind) => {
  if (isEmptyVal(av) && isEmptyVal(bv)) return false;
  if (kind === 'date') return isoDay(av) !== isoDay(bv);
  if (kind === 'enumList' || kind === 'list') return JSON.stringify([...asList(av)].sort()) !== JSON.stringify([...asList(bv)].sort());
  if (kind === 'number') return (isEmptyVal(av) ? null : Number(av)) !== (isEmptyVal(bv) ? null : Number(bv));
  if (kind === 'boolean' || kind === 'enum') return (av ?? null) !== (bv ?? null);
  return norm(av as string) !== norm(bv as string);
};

function Empty() {
  return <span className="text-gray-400 dark:text-gray-500">Not recorded</span>;
}

function Chips({ types }: { types: string[] }) {
  if (types.length === 0) return <Empty />;
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <span key={t} className={`px-1.5 py-0.5 text-[11px] rounded-full border ${getViolationColor(t).soft}`}>{getViolationTypeLabel(t)}</span>
      ))}
    </div>
  );
}

function Sources({ c }: { c: FullCase }) {
  const urls = urlsOf(c);
  if (urls.length === 0) return <Empty />;
  return (
    <ul className="space-y-0.5">
      {urls.map((u) => (
        <li key={u} className="truncate">
          {/^https?:\/\//i.test(u)
            ? <a href={u} target="_blank" rel="noreferrer" className="text-primary hover:underline" title={u}>{u.replace(/^https?:\/\/(www\.)?/i, '')}</a>
            : <span className="text-gray-700 dark:text-gray-300" title={u}>{u}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Render any field value for its kind. */
const show = (v: unknown, kind: Kind): React.ReactNode => {
  if (isEmptyVal(v)) return <Empty />;
  if (kind === 'date') return <span>{formatCaseDate(v as string)}</span>;
  if (kind === 'boolean') return <span>{v === true ? 'Yes' : 'No'}</span>;
  if (kind === 'enum') return <span>{enumLabel(String(v))}</span>;
  if (kind === 'enumList') return <span>{asList(v).map(enumLabel).join(', ')}</span>;
  if (kind === 'list') return <span>{asList(v).join(', ')}</span>;
  if (kind === 'number') return <span>{String(v)}</span>;
  if (kind === 'textarea') return <div className="max-h-48 overflow-y-auto pr-1"><MarkdownText text={String(v)} className="" /></div>;
  return <span>{String(v)}</span>;
};

export default function CaseCompare({
  match, onDecide, deciding, mode = 'review', canDecide = true,
}: {
  match: CompareMatch;
  onDecide: (status: 'CONFIRMED' | 'DISMISSED', extra?: DecideExtra) => void;
  deciding: boolean;
  /** 'merge' = raised by a researcher; the confirm button reads Merge. */
  mode?: 'review' | 'merge';
  /** False for roles that may look but not decide. */
  canDecide?: boolean;
}) {
  const { user } = useAuth();

  const incoming = useQuery<FullCase>({
    queryKey: ['case', match.case.id],
    queryFn: async () => (await API.get(`/api/cases/${match.case.id}`)).data.case,
  });
  const canonical = useQuery<FullCase>({
    queryKey: ['case', match.matchedCase?.id],
    queryFn: async () => (await API.get(`/api/cases/${match.matchedCase!.id}`)).data.case,
    enabled: !!match.matchedCase,
  });

  const a = incoming.data;   // the duplicate
  const b = canonical.data;  // the canonical
  const loading = incoming.isLoading || canonical.isLoading;
  const failed = incoming.error || canonical.error;
  const manual = match.source === 'MANUAL';
  const primaryCanonical = hasFirstHand(b);
  const locked = primaryCanonical || !canDecide;

  // Same masking rule as the case page: trends-only cases hide victim names from
  // Researchers and Documenters.
  const canSeeIds = user?.role === 'ADMIN' || user?.role === 'APPROVER';
  const hideNamesOf = (c?: FullCase) => !!c && c.dataUsageRestriction === 'IPRI_ONLY_TRENDS' && !canSeeIds;
  const victimLabel = (v: VictimRow, i: number, c?: FullCase) => (v.isGroup
    ? `Group${(v.groupName || []).length ? `: ${(v.groupName || []).join(', ')}` : ''}${v.groupSize ? ` · ${v.groupSize} people` : ''}`
    : (hideNamesOf(c) ? `Victim ${i + 1}` : (victimName(v) || `Victim ${i + 1}`)));

  /** Everything recorded about a victim, folded under a "details" toggle: the filled fields and the
   *  per-violation rows. Dates of birth follow the same masking rule as names. */
  const VictimDetails = ({ v, c }: { v: VictimRow; c?: FullCase }) => {
    const hide = hideNamesOf(c);
    const fields = VICTIM_DETAIL_FIELDS.filter(({ field }) => !isEmptyVal(v[field]) && !(hide && field === 'dateOfBirth'));
    const rows = v.violationDetails || [];
    if (fields.length === 0 && rows.length === 0) return <span className="text-xs text-gray-400 dark:text-gray-500">no further detail</span>;
    return (
      <details className="mt-1">
        <summary className="cursor-pointer select-none text-xs text-primary hover:underline">{plural(fields.length, 'detail')}{rows.length ? `, ${plural(rows.length, 'violation row')}` : ''}</summary>
        <dl className="mt-1 grid grid-cols-1 gap-y-0.5 text-xs">
          {fields.map(({ field, label, kind }) => (
            <div key={field} className="flex gap-2 min-w-0"><dt className="text-gray-500 dark:text-gray-400 shrink-0 w-32">{label}</dt><dd className="text-gray-800 dark:text-gray-200 min-w-0 break-words">{show(v[field], kind)}</dd></div>
          ))}
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2 min-w-0">
              <dt className="text-gray-500 dark:text-gray-400 shrink-0 w-32">{getViolationTypeLabel(r.violationType)}{r.otherViolationType ? ` · ${enumLabel(r.otherViolationType)}` : ''}</dt>
              <dd className="text-gray-800 dark:text-gray-200 min-w-0 break-words">
                {[
                  r.affectedCount != null ? `${r.affectedCount} affected` : '',
                  r.affectedMaleCount != null || r.affectedFemaleCount != null ? `${r.affectedMaleCount ?? 0} male / ${r.affectedFemaleCount ?? 0} female` : '',
                  r.accusationOutcome ? enumLabel(r.accusationOutcome) : '',
                  r.detentionType ? `${enumLabel(r.detentionType)}${r.detentionDays != null ? ` ${r.detentionDays} days` : ''}` : '',
                  (r.dueProcessViolations || []).length ? (r.dueProcessViolations || []).map(enumLabel).join(', ') : '',
                ].filter(Boolean).join(' · ') || 'recorded'}
              </dd>
            </div>
          ))}
        </dl>
      </details>
    );
  };

  // --- Choices --------------------------------------------------------------------------------
  // One entry per differing field, keyed `case:<field>` or `block:<type>:<field>`.
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const caseKey = (field: string) => `case:${field}`;
  const blockKey = (type: string, field: string) => `block:${type}:${field}`;

  const differingCase = useMemo(() => (a && b ? SCALAR_FIELDS.filter(({ field, kind }) => differs(a[field], b[field], kind)) : []), [a, b]);

  const blocksA = useMemo(() => a?.violationDetails || [], [a]);
  const blocksB = useMemo(() => b?.violationDetails || [], [b]);
  const blockBByType = useMemo(() => new Map(blocksB.map((x) => [x.violationType, x])), [blocksB]);
  const blocksOnlyOnDup = useMemo(() => blocksA.filter((x) => !blockBByType.has(x.violationType)), [blocksA, blockBByType]);
  const sharedBlocks = useMemo(() => blocksA.filter((x) => blockBByType.has(x.violationType)).map((da) => {
    const db = blockBByType.get(da.violationType)!;
    const fields = BLOCK_FIELDS.filter(({ field, kind }) => differs(da[field], db[field], kind));
    return { type: da.violationType, dup: da, canon: db, fields };
  }), [blocksA, blockBByType]);

  // Matched victim pairs (duplicate row ↔ canonical row, by name or group name) and what differs
  // between them, including their per-violation rows. The canonical row is the one written to.
  const victimPairs = useMemo(() => {
    if (!a || !b) return [] as Array<{ dup: VictimRow; canon: VictimRow; fields: typeof VICTIM_MERGE_FIELDS; vvd: Array<{ key: string; label: string; fields: typeof VVD_MERGE_FIELDS; dupRow: VictimViolationRow; canonRow?: VictimViolationRow }> }>;
    const bNamed = (b.victims || []).filter((v) => !v.isGroup); const bGroups = (b.victims || []).filter((v) => v.isGroup);
    const hide = hideNamesOf(a) || hideNamesOf(b);
    return (a.victims || []).map((dv) => {
      const cv = dv.isGroup ? bGroups.find((v) => groupKey(v) === groupKey(dv) && groupKey(dv)) : bNamed.find((v) => norm(victimName(v)) === norm(victimName(dv)) && victimName(dv));
      if (!cv) return null;
      const fields = VICTIM_MERGE_FIELDS.filter(({ field, kind }) => !(hide && field === 'dateOfBirth') && differs(dv[field], cv[field], kind));
      const vvd = (dv.violationDetails || []).map((r) => {
        const key = vvdKey(r); const cr = (cv.violationDetails || []).find((x) => vvdKey(x) === key);
        const f = VVD_MERGE_FIELDS.filter(({ field, kind }) => differs((r as Record<string, unknown>)[field], cr ? (cr as Record<string, unknown>)[field] : null, kind));
        return { key, label: key === 'KILLING' || key === 'CRIMINALIZATION' ? getViolationTypeLabel(key) : enumLabel(key), fields: f, dupRow: r, canonRow: cr };
      }).filter((x) => x.fields.length);
      return { dup: dv, canon: cv, fields, vvd };
    }).filter((x): x is NonNullable<typeof x> => !!x && (x.fields.length > 0 || x.vvd.length > 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, canSeeIds]);
  const victimKey = (canonId: string, field: string) => `vic:${canonId}:${field}`;
  const vvdFieldKey = (canonId: string, key: string, field: string) => `vvd:${canonId}:${key}:${field}`;

  useEffect(() => {
    if (!a || !b) return;
    setChoices((prev) => {
      const next = { ...prev };
      const seed = (key: string, av: unknown, bv: unknown, kind: Kind) => {
        if (next[key]) return;
        // Default: keep the original, unless it is blank and the duplicate is not (take it), or both
        // are coded lists (both). A primary-data original is never edited, so every row shows
        // "Keep original" there — the same as what the confirm sends, which is nothing.
        let pick: Pick = 'canonical';
        if (!primaryCanonical) {
          if (isEmptyVal(bv) && !isEmptyVal(av)) pick = 'duplicate';
          else if ((kind === 'enumList' || kind === 'list') && !isEmptyVal(av) && !isEmptyVal(bv)) pick = 'union';
        }
        // A fallback for the editor only: the original's text. openEditor replaces it with whichever
        // side is selected when the reviewer clicks "Write it".
        const custom = kind === 'textarea' || kind === 'text' ? (isEmptyVal(bv) ? '' : String(bv)) : (kind === 'date' ? isoDay(bv) : '');
        next[key] = { pick, custom };
      };
      for (const { field, kind } of differingCase) seed(caseKey(field), a[field], b[field], kind);
      for (const sb of sharedBlocks) for (const { field, kind } of sb.fields) seed(blockKey(sb.type, field), sb.dup[field], sb.canon[field], kind);
      for (const pr of victimPairs) {
        for (const { field, kind } of pr.fields) seed(victimKey(pr.canon.id, field), pr.dup[field], pr.canon[field], kind);
        for (const row of pr.vvd) for (const { field, kind } of row.fields) seed(vvdFieldKey(pr.canon.id, row.key, field), (row.dupRow as Record<string, unknown>)[field], row.canonRow ? (row.canonRow as Record<string, unknown>)[field] : null, kind);
      }
      return next;
    });
  }, [a, b, differingCase, sharedBlocks, victimPairs, primaryCanonical]);

  const setPick = (key: string, pick: Pick) => setChoices((p) => ({ ...p, [key]: { ...(p[key] || { custom: '' }), pick } }));
  // Keys whose text the reviewer has saved at least once: only those show a "Your text" option.
  // (Every writable field is pre-seeded for the editor, so the seed alone is not a choice.)
  const [choicesTouched, setChoicesTouched] = useState<Set<string>>(new Set());

  // "Write it" edits in a modal: a rich-text editor for narrative fields, a plain input for short
  // text, a date picker for dates. The box starts from whichever option is selected on the row (the
  // reviewer's own text if they saved one), and the other side's value is shown on request to copy
  // from. Cancel restores the previous choice; Use this saves and selects.
  const [editing, setEditing] = useState<{ key: string; label: string; kind: Kind; draft: string; previousPick: Pick; before: unknown; incoming: unknown; basis: 'canonical' | 'duplicate' } | null>(null);
  const openEditor = (key: string, label: string, kind: Kind, before: unknown, incoming: unknown) => {
    const ch = choices[key] || { pick: 'canonical' as Pick, custom: '' };
    const asText = (v: unknown) => (kind === 'date' ? isoDay(v) : (isEmptyVal(v) ? '' : String(v)));
    const basis: 'canonical' | 'duplicate' = ch.pick === 'duplicate' ? 'duplicate' : 'canonical';
    const draft = ch.pick === 'custom' && choicesTouched.has(key) ? ch.custom : (basis === 'duplicate' ? asText(incoming) : asText(before));
    setEditing({ key, label, kind, draft, previousPick: ch.pick, before, incoming, basis });
  };
  const saveEditor = () => {
    if (!editing) return;
    setChoices((p) => ({ ...p, [editing.key]: { pick: 'custom', custom: editing.draft } }));
    setChoicesTouched((s) => new Set(s).add(editing.key));
    setEditing(null);
  };
  // Stable identity: the Modal re-runs its focus effect whenever onClose changes, and a new
  // function per keystroke would move focus to its Close button on every character typed.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const cancelEditor = useCallback(() => {
    const e = editingRef.current;
    if (!e) return;
    if (e.previousPick !== 'custom') setChoices((p) => ({ ...p, [e.key]: { ...(p[e.key] || { custom: '' }), pick: e.previousPick } }));
    setEditing(null);
  }, []);

  // --- Entities -------------------------------------------------------------------------------
  const victimsA = useMemo(() => a?.victims || [], [a]);
  const matchedVictim = useMemo(() => {
    const names = new Set((b?.victims || []).filter((v) => !v.isGroup).map((v) => norm(victimName(v))).filter(Boolean));
    const groups = new Set((b?.victims || []).filter((v) => v.isGroup).map(groupKey).filter(Boolean));
    const m = new Map<string, boolean>();
    for (const v of victimsA) m.set(v.id, v.isGroup ? groups.has(groupKey(v)) : names.has(norm(victimName(v))));
    return m;
  }, [victimsA, b]);
  const perpsA = useMemo(() => a?.perpetrators || [], [a]);
  const matchedPerp = useMemo(() => {
    const ids = new Set((b?.perpetrators || []).map((p) => p.id));
    const names = new Set((b?.perpetrators || []).map((p) => norm(perpName(p))).filter(Boolean));
    const m = new Map<string, boolean>();
    for (const p of perpsA) m.set(p.id, ids.has(p.id) || names.has(norm(perpName(p))));
    return m;
  }, [perpsA, b]);

  // bring[id] is the reviewer's explicit tick. Nothing is copied unless ticked: a victim or
  // perpetrator only reaches the canonical when the reviewer says so, matched or not.
  const [bring, setBring] = useState<Record<string, boolean>>({});
  const bringing = (id: string, _matched: boolean) => (bring[id] ?? false);
  const [copyBlock, setCopyBlock] = useState<Record<string, boolean>>({});
  const copying = (id: string) => copyBlock[id] ?? true;
  // Development projects on the duplicate, matched to the canonical's by name; copied only when ticked.
  const projectsA = useMemo(() => a?.developmentProjects || [], [a]);
  const matchedProject = useMemo(() => {
    const names = new Set((b?.developmentProjects || []).map((p) => norm(p.name || '')).filter(Boolean));
    const m = new Map<string, boolean>();
    for (const p of projectsA) m.set(p.id, !!p.name && names.has(norm(p.name)));
    return m;
  }, [projectsA, b]);
  const [copyProject, setCopyProject] = useState<Record<string, boolean>>({});
  const copyingProject = (id: string) => copyProject[id] ?? false;
  // Trends-only duplicate into an unrestricted canonical: the reviewer must acknowledge it.
  const restrictionMismatch = !!a && !!b && a.dataUsageRestriction === 'IPRI_ONLY_TRENDS' && b.dataUsageRestriction !== 'IPRI_ONLY_TRENDS';
  const [ackRestriction, setAckRestriction] = useState(false);

  const newSources = useMemo(() => {
    if (!a || !b) return [] as string[];
    const bUrls = new Set(urlsOf(b).map(normUrl));
    return urlsOf(a).filter((u) => !bUrls.has(normUrl(u)));
  }, [a, b]);

  // --- Outcomes sent with the confirm ----------------------------------------------------------
  const valueFor = (key: string, av: unknown, bv: unknown, kind: Kind): { set: boolean; value?: unknown } => {
    const ch = choices[key];
    if (!ch || ch.pick === 'canonical') return { set: false };
    if (ch.pick === 'duplicate') return { set: true, value: kind === 'date' ? isoDay(av) || null : (av ?? null) };
    if (ch.pick === 'union') return { set: true, value: Array.from(new Set([...asList(bv), ...asList(av)])) };
    const v = ch.custom.trim();
    if (kind === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(v)) return { set: false };
    return { set: true, value: v === '' ? null : v };
  };
  const buildExtra = (): DecideExtra => {
    if (!a || !b || primaryCanonical) return {};
    const fieldOutcomes: FieldOutcome[] = [];
    for (const { field, kind } of differingCase) {
      const r = valueFor(caseKey(field), a[field], b[field], kind);
      if (r.set) fieldOutcomes.push({ field, value: r.value });
    }
    const blockFieldOutcomes: BlockFieldOutcome[] = [];
    for (const sb of sharedBlocks) for (const { field, kind } of sb.fields) {
      const r = valueFor(blockKey(sb.type, field), sb.dup[field], sb.canon[field], kind);
      if (r.set) blockFieldOutcomes.push({ violationType: sb.type, field, value: r.value });
    }
    const victimFieldOutcomes: VictimFieldOutcome[] = [];
    for (const pr of victimPairs) {
      for (const { field, kind } of pr.fields) { const r = valueFor(victimKey(pr.canon.id, field), pr.dup[field], pr.canon[field], kind); if (r.set) victimFieldOutcomes.push({ victimId: pr.canon.id, field, value: r.value }); }
      for (const row of pr.vvd) for (const { field, kind } of row.fields) { const r = valueFor(vvdFieldKey(pr.canon.id, row.key, field), (row.dupRow as Record<string, unknown>)[field], row.canonRow ? (row.canonRow as Record<string, unknown>)[field] : null, kind); if (r.set) victimFieldOutcomes.push({ victimId: pr.canon.id, violation: row.key, field, value: r.value }); }
    }
    const entityOutcomes: EntityOutcomes = {
      victimIds: victimsA.filter((v) => bringing(v.id, matchedVictim.get(v.id) ?? false)).map((v) => v.id),
      perpetratorIds: perpsA.filter((p) => bringing(p.id, matchedPerp.get(p.id) ?? false)).map((p) => p.id),
      violationDetailIds: blocksOnlyOnDup.filter((x) => copying(x.id)).map((x) => x.id),
      developmentProjectIds: projectsA.filter((p) => copyingProject(p.id)).map((p) => p.id),
    };
    return { fieldOutcomes, blockFieldOutcomes, victimFieldOutcomes, entityOutcomes, ...(restrictionMismatch ? { acknowledgeRestriction: ackRestriction } : {}) };
  };

  // Running totals for the footer sentence.
  const totals = useMemo(() => {
    const ex = buildExtra();
    return {
      fields: (ex.fieldOutcomes?.length || 0) + (ex.blockFieldOutcomes?.length || 0) + (ex.victimFieldOutcomes?.length || 0),
      victims: ex.entityOutcomes?.victimIds.length || 0,
      perps: ex.entityOutcomes?.perpetratorIds.length || 0,
      blocks: ex.entityOutcomes?.violationDetailIds.length || 0,
      projects: ex.entityOutcomes?.developmentProjectIds.length || 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choices, bring, copyBlock, copyProject, differingCase, sharedBlocks, victimPairs, victimsA, perpsA, blocksOnlyOnDup, projectsA, matchedVictim, matchedPerp, primaryCanonical, restrictionMismatch, ackRestriction]);

  // --- Basis: facts about the pair, before any score ------------------------------------------
  const basis = useMemo(() => {
    if (!a || !b) return [];
    const facts: string[] = [];
    if (a.country && b.country) facts.push(norm(a.country) === norm(b.country) ? 'same country' : 'different countries');
    if (a.indigenousGroup && b.indigenousGroup) facts.push(norm(a.indigenousGroup) === norm(b.indigenousGroup) ? 'same Indigenous group' : 'different Indigenous groups');
    const da = a.incidentDate || a.caseStartDate; const db = b.incidentDate || b.caseStartDate;
    if (da && db) {
      const days = Math.round(Math.abs(new Date(da).getTime() - new Date(db).getTime()) / 86400000);
      facts.push(days === 0 ? 'same date' : `dates ${days} day${days === 1 ? '' : 's'} apart`);
    }
    const shared = victimsA.filter((v) => !v.isGroup && matchedVictim.get(v.id)).map((v, i) => victimLabel(v, i, a));
    if (shared.length) facts.push(hideNamesOf(a) || hideNamesOf(b) ? `${shared.length} shared victim name${shared.length === 1 ? '' : 's'}` : `shared victim "${shared[0]}"${shared.length > 1 ? ` +${shared.length - 1}` : ''}`);
    const urlsB = new Set(urlsOf(b).map(normUrl));
    const sharedUrls = urlsOf(a).filter((u) => urlsB.has(normUrl(u))).length;
    if (sharedUrls) facts.push(`${sharedUrls} shared source${sharedUrls === 1 ? '' : 's'}`);
    return facts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, victimsA, matchedVictim, canSeeIds]);

  // --- Display-only rows ----------------------------------------------------------------------
  const dateOf = (c?: FullCase) => {
    if (!c) return '';
    const start = c.caseStartDate ? formatCaseDate(c.caseStartDate, c.caseStartDatePrecision) : (c.incidentDate ? formatCaseDate(c.incidentDate) : '');
    if (!start) return '';
    if (c.isCaseOngoing) return `${start} – ongoing`;
    return c.caseEndDate ? `${start} – ${formatCaseDate(c.caseEndDate)}` : start;
  };
  type Row = { label: string; left: React.ReactNode; right: React.ReactNode; same?: boolean | null };
  const rows: Row[] = a && b ? [
    { label: 'Status', left: STATUS_LABEL[a.status] || a.status, right: STATUS_LABEL[b.status] || b.status, same: null },
    { label: 'When', left: dateOf(a) || <Empty />, right: dateOf(b) || <Empty />, same: !!dateOf(a) && !!dateOf(b) ? norm(dateOf(a)) === norm(dateOf(b)) : null },
    { label: 'Country', left: a.country || <Empty />, right: b.country || <Empty />, same: !!a.country && !!b.country ? norm(a.country) === norm(b.country) : null },
    { label: 'Violations', left: <Chips types={a.violationTypes || []} />, right: <Chips types={b.violationTypes || []} />, same: null },
    { label: 'Development projects', left: (a.developmentProjects || []).map((p) => p.name).filter(Boolean).join('; ') || a.developmentProjectName || <Empty />, right: (b.developmentProjects || []).map((p) => p.name).filter(Boolean).join('; ') || b.developmentProjectName || <Empty />, same: null },
    { label: 'Sources', left: <Sources c={a} />, right: <Sources c={b} />, same: null },
    {
      label: 'Recorded',
      left: <span>{a.isAutoSourced ? 'AI-sourced' : (a.dataSource || 'Manual')}{a.createdAt ? ` · ${formatIsoDate(a.createdAt) || ''}` : ''}</span>,
      right: <span>{b.isAutoSourced ? 'AI-sourced' : (b.dataSource || 'Manual')}{b.createdAt ? ` · ${formatIsoDate(b.createdAt) || ''}` : ''}</span>,
      same: null,
    },
  ] : [];
  const agreeingScalars = a && b ? SCALAR_FIELDS.filter((f) => !differingCase.includes(f) && (!isEmptyVal(a[f.field]) || !isEmptyVal(b[f.field]))) : [];
  const [showAgreeing, setShowAgreeing] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ people: true, blocks: true });
  const toggle = (k: string) => setOpenSections((s) => ({ ...s, [k]: !(s[k] ?? true) }));

  const sim = Math.round(match.similarityScore * 100);
  const dupNo = match.case.caseNumber;
  const canonNo = match.matchedCase?.caseNumber ?? 'the existing case';
  const grid = 'grid grid-cols-[9rem_1fr_1fr] gap-x-4 px-6';
  // The two records are told apart by words, not colour: "original" is the one that stays and
  // receives the merge, "duplicate" is the one archived. Both numbers are plain bold text.
  const Canon = () => <span className="font-semibold text-gray-900 dark:text-gray-100">{canonNo}</span>;
  const Dup = () => <span className="font-semibold text-gray-700 dark:text-gray-200">{dupNo}</span>;

  /** A differing-field row with the three-way (or four-way, for coded lists) choice. */
  const choiceRow = (key: string, label: string, kind: Kind, av: unknown, bv: unknown, extraNote?: string) => {
    const ch = choices[key] || { pick: 'canonical' as Pick, custom: '' };
    const opt = (pick: Pick, title: React.ReactNode, body: React.ReactNode, onSelect?: () => void) => (
      <label className={`flex items-start gap-2 rounded-md border px-3 py-2 ${locked ? 'opacity-60' : 'cursor-pointer'} ${ch.pick === pick ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700'}`}>
        <input type="radio" name={`c-${key}`} disabled={locked} checked={ch.pick === pick} onChange={() => (onSelect ? onSelect() : setPick(key, pick))} className="mt-1 h-4 w-4 border-gray-300 text-primary focus:ring-primary" />
        <span className="min-w-0 flex-1 text-sm text-gray-800 dark:text-gray-200">
          <span className="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</span>
          {body}
        </span>
      </label>
    );
    const writable = kind === 'text' || kind === 'textarea' || kind === 'date';
    const unionable = (kind === 'enumList' || kind === 'list') && !isEmptyVal(av) && !isEmptyVal(bv);
    const hasCustom = writable && ch.custom.trim() !== '' && (ch.pick === 'custom' || choicesTouched.has(key));
    const cols = 2 + (unionable ? 1 : 0) + (hasCustom ? 1 : 0);
    return (
      <div key={key} className={`${grid} py-3 border-b border-gray-100 dark:border-gray-700/60 text-sm`}>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-0.5">
          {label}
          <span className="block mt-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400">differs</span>
          {isEmptyVal(bv) && !isEmptyVal(av) && ch.pick === 'duplicate' && <span className="block mt-0.5 text-[10px] text-gray-600 dark:text-gray-300">✓ more complete</span>}
          {extraNote && <span className="block mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">{extraNote}</span>}
        </div>
        <div className="col-span-2 min-w-0">
          {/* Top-right of the item: the one action that is not a pick. */}
          {writable && !locked && (
            <div className="flex justify-end mb-1.5">
              <button
                type="button"
                onClick={() => openEditor(key, label, kind, bv, av)}
                title={kind === 'textarea' ? 'Opens an editor starting from the option selected on this row' : 'Type a value by hand, starting from the option selected on this row'}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {hasCustom ? 'Edit your text' : 'Write it…'}
              </button>
            </div>
          )}
          <div className={`grid grid-cols-1 gap-2 ${cols >= 4 ? 'lg:grid-cols-4' : cols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
            {opt('duplicate', 'Take from duplicate', show(av, kind))}
            {opt('canonical', 'Keep original', show(bv, kind))}
            {unionable && opt('union', 'Both', <span>{Array.from(new Set([...asList(bv), ...asList(av)])).map((x) => (kind === 'list' ? x : enumLabel(x))).join(', ')}</span>)}
            {hasCustom && opt('custom', 'Your text', show(ch.custom, kind))}
          </div>
        </div>
      </div>
    );
  };

  const sectionHeader = (k: string, title: string, summary: React.ReactNode) => (
    <button type="button" onClick={() => toggle(k)} className="w-full text-left px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 hover:bg-gray-50 dark:hover:bg-gray-700/40 flex items-center gap-2">
      {(openSections[k] ?? true) ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{summary}</span>
    </button>
  );


  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto">
        {/* Basis first, engine second */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-sm space-y-1.5">
          {basis.length > 0 && <p className="text-gray-900 dark:text-gray-100"><span className="font-medium">Basis:</span> {basis.join(' · ')}</p>}
          {manual ? (
            <p className="text-gray-700 dark:text-gray-300"><span className="font-medium">Engine:</span> none — {match.llmReasoning || 'marked by hand'}{sim > 0 ? ` · content similarity ${sim}%` : ''}.</p>
          ) : (
            <>
              <p className="text-gray-900 dark:text-gray-100"><span className="font-medium">Engine:</span> <span className="font-semibold tabular-nums">{match.llmConfidence}</span> compare score · <span className="font-semibold tabular-nums">{sim}%</span> content similarity</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Content similarity is how alike the two descriptions read, which is what paired them. The compare score is the engine's judgement, after reading both cases, that they describe one incident.</p>
              {match.llmReasoning && <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">“{match.llmReasoning}”</p>}
            </>
          )}
        </div>

        {/* Cautions: facts a skimming reviewer must not miss. The restriction one gates Merge. */}
        {a && b && (() => {
          const cautions: string[] = [];
          if (a.country && b.country && norm(a.country) !== norm(b.country)) cautions.push(`Different countries: ${a.country} and ${b.country}.`);
          if (a.indigenousGroup && b.indigenousGroup && norm(a.indigenousGroup) !== norm(b.indigenousGroup)) cautions.push(`Different Indigenous groups: ${a.indigenousGroup} and ${b.indigenousGroup}.`);
          const da = a.incidentDate || a.caseStartDate; const db = b.incidentDate || b.caseStartDate;
          if (da && db) { const days = Math.round(Math.abs(new Date(da).getTime() - new Date(db).getTime()) / 86400000); if (days > 30) cautions.push(`Dates ${days} days apart: two write-ups of one event rarely differ by more than a few days.`); }
          if (!cautions.length && !restrictionMismatch) return null;
          return (
            <div className="px-6 py-3 border-b border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-900 dark:text-amber-200 space-y-1.5">
              {cautions.map((c) => <p key={c}>⚠ {c}</p>)}
              {restrictionMismatch && (
                <div>
                  <p>⚠ <Dup /> is restricted to trends-only use and <Canon /> is not. Merging moves restricted text, victims and details into an unrestricted record that can be exported and shared.</p>
                  <label className="mt-1 inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={ackRestriction} onChange={(e) => setAckRestriction(e.target.checked)} disabled={!canDecide} className="h-4 w-4 rounded border-amber-400 text-primary focus:ring-primary" />
                    <span className="font-medium">I have checked what is being carried over and accept that it leaves the restriction.</span>
                  </label>
                </div>
              )}
            </div>
          );
        })()}

        {loading && <div className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">Loading both cases…</div>}
        {!loading && failed && <div className="px-6 py-12 text-center text-sm text-red-700 dark:text-red-300">Couldn't load one of the cases. {failed instanceof Error ? failed.message : ''}</div>}

        {!loading && !failed && a && b && (
          <div>
            <div className={`${grid} py-2 border-b border-gray-200 dark:border-gray-700 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 sticky top-0 bg-white dark:bg-gray-800 z-10`}>
              <span />
              <span className="truncate">Duplicate · <Dup />{hasFirstHand(a) ? ' · first-hand' : ''}<span className="ml-2 normal-case tracking-normal font-normal text-gray-400 dark:text-gray-500">archived on {mode === 'merge' ? 'merge' : 'confirm'}</span></span>
              <span className="truncate">Original · <Canon />{hasFirstHand(b) ? ' · first-hand' : ''}<span className="ml-2 normal-case tracking-normal font-normal text-gray-400 dark:text-gray-500">kept, receives the merge</span></span>
            </div>

            {/* Display-only rows */}
            {rows.map((row) => (
              <div key={row.label} className={`${grid} py-3 border-b border-gray-100 dark:border-gray-700/60 text-sm`}>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-0.5">
                  {row.label}
                  {row.same === true && <span className="block mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">same</span>}
                  {row.same === false && <span className="block mt-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400">differs</span>}
                </div>
                <div className="text-gray-900 dark:text-gray-100 min-w-0 break-words">{row.left}</div>
                <div className="text-gray-900 dark:text-gray-100 min-w-0 break-words">{row.right}</div>
              </div>
            ))}

            {/* 1. Case fields */}
            <div className="px-6 pt-4 pb-1">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">What the original will say</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {primaryCanonical
                  ? 'Primary record: its fields are not edited from a secondary write-up. The duplicate is archived and its sources attached.'
                  : `For each field where the two differ, choose what the original keeps. Applied on ${mode === 'merge' ? 'merge' : 'confirm'}; nothing is reworded by the engine.`}
              </p>
            </div>
            {differingCase.length === 0 && <div className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400">No case field differs between the two records.</div>}
            {differingCase.map(({ field, label, kind }) => choiceRow(caseKey(field), label, kind, a[field], b[field]))}
            {agreeingScalars.length > 0 && (
              <>
                <button type="button" onClick={() => setShowAgreeing((s) => !s)} className="w-full text-left px-6 py-2 border-b border-gray-100 dark:border-gray-700/60 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40 inline-flex items-center gap-1">
                  {showAgreeing ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                  {plural(agreeingScalars.length, 'field')} agree
                </button>
                {showAgreeing && agreeingScalars.map(({ field, label, kind }) => (
                  <div key={field} className={`${grid} py-2 border-b border-gray-100 dark:border-gray-700/60 text-sm`}>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-0.5">{label}<span className="block mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">same</span></div>
                    <div className="col-span-2 text-gray-800 dark:text-gray-200 break-words">{show(b[field], kind)}</div>
                  </div>
                ))}
              </>
            )}

            {/* 2. People */}
            {sectionHeader('people', 'People',
              <>{plural(victimsA.length, 'victim')} on the duplicate: {victimsA.filter((v) => matchedVictim.get(v.id)).length} already on the original, {totals.victims} ticked to copy · {plural(perpsA.length, 'perpetrator')}: {perpsA.filter((p) => matchedPerp.get(p.id)).length} already, {totals.perps} ticked to link</>)}
            {(openSections.people ?? true) && (
              <div>
                {/* Victims: the duplicate's with their bring-over control on the left, the canonical's
                    read-only on the right, each with its full recorded detail folded underneath. */}
                <div className={`${grid} py-3 border-b border-gray-100 dark:border-gray-700/60 text-sm`}>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-0.5">Victims</div>
                  <div className="min-w-0">
                    {victimsA.length === 0 ? <Empty /> : (
                      <ul className="space-y-2">
                        {victimsA.map((v, i) => {
                          const matched = matchedVictim.get(v.id) ?? false;
                          const on = bringing(v.id, matched);
                          const rows = (v.violationDetails || []).length;
                          return (
                            <li key={v.id} className={`rounded-md border px-3 py-2 ${on ? 'border-primary/60 bg-primary/5' : 'border-gray-200 dark:border-gray-700'}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <span className="font-medium text-gray-900 dark:text-gray-100">{victimLabel(v, i, a)}</span>
                                  {v.isIndigenous === false && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Non-Indigenous</span>}
                                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    {matched
                                      ? <>already on the original (matched by {v.isGroup ? 'group name' : 'name'})</>
                                      : <>not on the original</>}
                                  </div>
                                </div>
                                <label className={`inline-flex items-center gap-2 shrink-0 ${locked ? 'opacity-60' : 'cursor-pointer'}`} title={matched ? `The name match may be wrong: tick to copy this ${v.isGroup ? 'group' : 'person'} to ${b.caseNumber} as a separate victim` : `Tick to copy this ${v.isGroup ? 'group' : 'person'} to ${b.caseNumber}${rows ? ` with ${plural(rows, 'violation row')}` : ''}`}>
                                  <input type="checkbox" disabled={locked} checked={on} onChange={(e) => setBring((p) => ({ ...p, [v.id]: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                                    {matched ? <>Not the same {v.isGroup ? 'group' : 'person'} — copy anyway</> : <>Copy to original</>}
                                  </span>
                                </label>
                              </div>
                              {on && (
                                <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                                  On merge: added to the original as {matched ? 'a separate victim' : 'a new victim'}{rows ? `, with ${plural(rows, 'violation row')}` : ''}. The duplicate keeps its own copy.
                                </div>
                              )}
                              <VictimDetails v={v} c={a} />
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <div className="min-w-0">
                    {(b.victims || []).length === 0 ? <Empty /> : (
                      <ul className="space-y-2">
                        {(b.victims || []).map((v, i) => (
                          <li key={v.id} className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <span className="font-medium text-gray-900 dark:text-gray-100">{victimLabel(v, i, b)}</span>
                              {v.isIndigenous === false && <span className="text-xs text-gray-500 dark:text-gray-400">Non-Indigenous</span>}
                              <span className="text-xs text-gray-600 dark:text-gray-300">on the original, kept</span>
                            </div>
                            <VictimDetails v={v} c={b} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Perpetrators: same shape; linking, not copying, since they are a shared registry. */}
                <div className={`${grid} py-3 border-b border-gray-100 dark:border-gray-700/60 text-sm`}>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-0.5">Perpetrators</div>
                  <div className="min-w-0">
                    {perpsA.length === 0 ? <Empty /> : (
                      <ul className="space-y-1.5">
                        {perpsA.map((p) => {
                          const matched = matchedPerp.get(p.id) ?? false;
                          const on = bringing(p.id, matched);
                          return (
                            <li key={p.id} className={`rounded-md border px-3 py-2 flex items-start justify-between gap-3 ${on ? 'border-primary/60 bg-primary/5' : 'border-gray-200 dark:border-gray-700'}`}>
                              <div className="min-w-0 flex-1">
                                <span className="font-medium text-gray-900 dark:text-gray-100">{perpName(p) || 'Unnamed perpetrator'}</span>
                                {p.type && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{p.type}</span>}
                                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{matched ? <>already on the original</> : <>not on the original</>}</div>
                                {on && <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">On merge: linked to the original. Perpetrators are a shared list, so this is a link, not a copy.</div>}
                              </div>
                              {!matched && (
                                <label className={`inline-flex items-center gap-2 shrink-0 ${locked ? 'opacity-60' : 'cursor-pointer'}`} title={`Tick to link this perpetrator to ${b.caseNumber}`}>
                                  <input type="checkbox" disabled={locked} checked={on} onChange={(e) => setBring((q) => ({ ...q, [p.id]: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Link to original</span>
                                </label>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <div className="min-w-0">
                    {(b.perpetrators || []).length === 0 ? <Empty /> : (
                      <ul className="space-y-1.5">
                        {(b.perpetrators || []).map((p) => (
                          <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="font-medium text-gray-900 dark:text-gray-100">{perpName(p) || 'Unnamed perpetrator'}</span>
                            {p.type && <span className="text-gray-500 dark:text-gray-400">{p.type}</span>}
                            <span className="text-xs text-gray-600 dark:text-gray-300">on the original, kept</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <p className="px-6 py-2 text-xs text-gray-500 dark:text-gray-400">Nothing here is copied unless you tick it. A merge only adds: nothing on the original is removed, and the duplicate keeps all of its own rows in the archive. Un-merge removes whatever was copied.</p>
              </div>
            )}

            {/* 3. Violation detail blocks */}
            {sectionHeader('blocks', 'Violation details',
              <>{plural(blocksOnlyOnDup.length, 'block')} only on the duplicate, {totals.blocks} to bring over · {plural(sharedBlocks.length, 'block')} on both, {sharedBlocks.reduce((n, sb) => n + sb.fields.length, 0)} differing field{sharedBlocks.reduce((n, sb) => n + sb.fields.length, 0) === 1 ? '' : 's'}</>)}
            {(openSections.blocks ?? true) && (
              <div>
                {blocksOnlyOnDup.length === 0 && sharedBlocks.length === 0 && <p className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400">No violation-detail blocks on the duplicate.</p>}
                {blocksOnlyOnDup.map((x) => {
                  const filled = BLOCK_FIELDS.filter(({ field }) => !isEmptyVal(x[field]));
                  return (
                    <div key={x.id} className="px-6 py-3 border-b border-gray-100 dark:border-gray-700/60 text-sm">
                      <label className={`inline-flex items-center gap-2 ${locked ? 'opacity-60' : 'cursor-pointer'}`}>
                        <input type="checkbox" disabled={locked} checked={copying(x.id)} onChange={(e) => setCopyBlock((p) => ({ ...p, [x.id]: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                        <span className={`px-1.5 py-0.5 text-[11px] rounded-full border ${getViolationColor(x.violationType).soft}`}>{getViolationTypeLabel(x.violationType)}</span>
                        <span className="text-gray-700 dark:text-gray-300">only on the duplicate · {copying(x.id) ? <>brought over whole to the original</> : 'left out'} · {plural(filled.length, 'field')} filled</span>
                      </label>
                      {filled.length > 0 && (
                        <dl className="mt-2 ml-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                          {filled.slice(0, 8).map(({ field, label, kind }) => (
                            <div key={field} className="flex gap-2 min-w-0"><dt className="text-gray-500 dark:text-gray-400 shrink-0">{label}:</dt><dd className="text-gray-800 dark:text-gray-200 truncate">{kind === 'textarea' ? String(x[field]).slice(0, 120) : show(x[field], kind)}</dd></div>
                          ))}
                          {filled.length > 8 && <div className="text-gray-400 dark:text-gray-500">and {filled.length - 8} more</div>}
                        </dl>
                      )}
                    </div>
                  );
                })}
                {sharedBlocks.map((sb) => (
                  <div key={sb.type}>
                    <div className="px-6 py-2 border-b border-gray-100 dark:border-gray-700/60 flex items-center gap-2 text-sm">
                      <span className={`px-1.5 py-0.5 text-[11px] rounded-full border ${getViolationColor(sb.type).soft}`}>{getViolationTypeLabel(sb.type)}</span>
                      <span className="text-gray-700 dark:text-gray-300">on both · {sb.fields.length === 0 ? 'no field differs' : `${plural(sb.fields.length, 'field')} differ${sb.fields.length === 1 ? 's' : ''}`}</span>
                    </div>
                    {sb.fields.map(({ field, label, kind }) => choiceRow(blockKey(sb.type, field), label, kind, sb.dup[field], sb.canon[field], getViolationTypeLabel(sb.type)))}
                  </div>
                ))}
              </div>
            )}

            {/* 3b. Matched victims: what the duplicate's row says that the canonical's lacks or contradicts */}
            {victimPairs.length > 0 && (
              <>
                {sectionHeader('victimFields', 'Matched victims, details that differ', <>{plural(victimPairs.length, 'victim')} on both records with {victimPairs.reduce((n, pr) => n + pr.fields.length + pr.vvd.reduce((m, r) => m + r.fields.length, 0), 0)} differing field{victimPairs.reduce((n, pr) => n + pr.fields.length + pr.vvd.reduce((m, r) => m + r.fields.length, 0), 0) === 1 ? '' : 's'} — written to the original's row</>)}
                {(openSections.victimFields ?? true) && victimPairs.map((pr, i) => (
                  <div key={pr.canon.id}>
                    <div className="px-6 py-2 border-b border-gray-100 dark:border-gray-700/60 text-sm text-gray-700 dark:text-gray-300"><span className="font-medium text-gray-900 dark:text-gray-100">{victimLabel(pr.canon, i, b)}</span> · on both records</div>
                    {pr.fields.map(({ field, label, kind }) => choiceRow(victimKey(pr.canon.id, field), label, kind, pr.dup[field], pr.canon[field], victimLabel(pr.canon, i, b)))}
                    {pr.vvd.map((row) => row.fields.map(({ field, label, kind }) => choiceRow(vvdFieldKey(pr.canon.id, row.key, field), `${row.label}: ${label}`, kind, (row.dupRow as Record<string, unknown>)[field], row.canonRow ? (row.canonRow as Record<string, unknown>)[field] : null, victimLabel(pr.canon, i, b))))}
                  </div>
                ))}
              </>
            )}

            {/* 3c. Development projects */}
            {sectionHeader('projects', 'Development projects', <>{plural(projectsA.length, 'project')} on the duplicate: {projectsA.filter((p) => matchedProject.get(p.id)).length} already on the original, {totals.projects} ticked to copy</>)}
            {(openSections.projects ?? true) && (
              <div className={`${grid} py-3 border-b border-gray-100 dark:border-gray-700/60 text-sm`}>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-0.5">Projects</div>
                <div className="min-w-0">
                  {projectsA.length === 0 ? <Empty /> : (
                    <ul className="space-y-2">
                      {projectsA.map((p) => {
                        const matched = matchedProject.get(p.id) ?? false; const on = copyingProject(p.id);
                        return (
                          <li key={p.id} className={`rounded-md border px-3 py-2 ${on ? 'border-primary/60 bg-primary/5' : 'border-gray-200 dark:border-gray-700'}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <span className="font-medium text-gray-900 dark:text-gray-100">{p.name || 'Unnamed project'}</span>
                                {p.type && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{enumLabel(String(p.type))}</span>}
                                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{matched ? <>already on the original (matched by name)</> : <>not on the original</>}</div>
                              </div>
                              <label className={`inline-flex items-center gap-2 shrink-0 ${locked ? 'opacity-60' : 'cursor-pointer'}`} title={matched ? 'The name match may be wrong: tick to copy this project as a separate row' : 'Tick to copy this project to the canonical'}>
                                <input type="checkbox" disabled={locked} checked={on} onChange={(e) => setCopyProject((q) => ({ ...q, [p.id]: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{matched ? 'Not the same project — copy anyway' : <>Copy to original</>}</span>
                              </label>
                            </div>
                            {on && <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">On merge: added to the original as a project row. The duplicate keeps its own copy.</div>}
                            {(p.description || (p.subtypes || []).length || p.startYear || p.source || p.websiteLink) ? (
                              <details className="mt-1"><summary className="cursor-pointer select-none text-xs text-primary hover:underline">details</summary>
                                <dl className="mt-1 grid grid-cols-1 gap-y-0.5 text-xs">
                                  {(p.subtypes || []).length ? <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32 shrink-0">Subtypes</dt><dd className="text-gray-800 dark:text-gray-200">{(p.subtypes || []).map(enumLabel).join(', ')}</dd></div> : null}
                                  {p.startYear ? <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32 shrink-0">Start year</dt><dd className="text-gray-800 dark:text-gray-200">{p.startYear}</dd></div> : null}
                                  {p.description ? <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32 shrink-0">Description</dt><dd className="text-gray-800 dark:text-gray-200 break-words">{p.description}</dd></div> : null}
                                  {p.source ? <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32 shrink-0">Source</dt><dd className="text-gray-800 dark:text-gray-200 break-words">{p.source}</dd></div> : null}
                                  {p.websiteLink ? <div className="flex gap-2"><dt className="text-gray-500 dark:text-gray-400 w-32 shrink-0">Website</dt><dd className="text-gray-800 dark:text-gray-200 break-words">{p.websiteLink}</dd></div> : null}
                                </dl>
                              </details>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="min-w-0">
                  {(b.developmentProjects || []).length === 0 ? <Empty /> : (
                    <ul className="space-y-2">
                      {(b.developmentProjects || []).map((p) => (
                        <li key={p.id} className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{p.name || 'Unnamed project'}</span>
                          {p.type && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{enumLabel(String(p.type))}</span>}
                          <span className="ml-2 text-xs text-gray-600 dark:text-gray-300">on the original, kept</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* 4. Always attached */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 text-sm">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Always attached</h4>
              <p className="mt-1 text-gray-700 dark:text-gray-300">
                {newSources.length ? <>{plural(newSources.length, 'source')} from the duplicate join{newSources.length === 1 ? 's' : ''} the original's sources</> : <>No new sources (all already on the original)</>}; its documents move over and its comments are copied with a “[from <Dup />]” prefix. All of it comes back on un-merge.
              </p>
            </div>

            <div className="px-6 py-3 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-4">
              <Link to={`/cases/${a.id}`} className="inline-flex items-center gap-1 hover:underline">Open {a.caseNumber} <ArrowTopRightOnSquareIcon className="h-3 w-3" /></Link>
              <Link to={`/cases/${b.id}`} className="inline-flex items-center gap-1 hover:underline">Open {b.caseNumber} <ArrowTopRightOnSquareIcon className="h-3 w-3" /></Link>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={`${editing.label}: write what ${canonNo} will say`}
          description={editing.kind === 'textarea' ? `The box starts with ${editing.previousPick === 'custom' && choicesTouched.has(editing.key) ? 'your saved text' : `what ${editing.basis === 'duplicate' ? dupNo : canonNo} says now`}. Combine, cut or correct; formatting is kept on the case page and the PDF.` : undefined}
          onClose={cancelEditor}
          size={editing.kind === 'textarea' ? 'lg' : 'sm'}
        >
          <div className="space-y-3">
            {/* The editor starts from the selected side. The other side's text is the other half of the
                merge, shown on request so it can be read and copied from while rewriting. */}
            {(() => {
              const refIsKept = editing.basis === 'duplicate';
              const refVal = refIsKept ? editing.before : editing.incoming;
              const refNo = refIsKept ? canonNo : dupNo;
              return (
                <details className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                  <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-primary hover:underline">Show what {refNo} says, to copy from</summary>
                  <div className="px-3 pb-3 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">{refIsKept ? 'The original says' : 'The duplicate says'}</span>
                      {!isEmptyVal(refVal) && (
                        <button type="button" onClick={() => { navigator.clipboard?.writeText(editing.kind === 'date' ? isoDay(refVal) : String(refVal)).catch(() => {}); }} className="text-xs text-primary hover:underline shrink-0" title="Copy to clipboard">Copy</button>
                      )}
                    </div>
                    <div className="text-sm text-gray-800 dark:text-gray-200 break-words max-h-40 overflow-y-auto pr-1">{show(refVal, editing.kind)}</div>
                  </div>
                </details>
              );
            })()}
            {editing.kind === 'textarea' ? (
              <RichTextEditor value={editing.draft} onChange={(v) => setEditing((e) => (e ? { ...e, draft: v } : e))} rows={14} placeholder="Write the combined text…" />
            ) : editing.kind === 'date' ? (
              <input type="date" autoFocus value={editing.draft} onChange={(e) => setEditing((s) => (s ? { ...s, draft: e.target.value } : s))} className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:border-primary focus:ring-primary" />
            ) : (
              <input type="text" autoFocus value={editing.draft} onChange={(e) => setEditing((s) => (s ? { ...s, draft: e.target.value } : s))} className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:border-primary focus:ring-primary" />
            )}
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={cancelEditor} className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button type="button" onClick={saveEditor} disabled={editing.kind === 'date' && !!editing.draft && !/^\d{4}-\d{2}-\d{2}$/.test(editing.draft)} className="px-3 py-1.5 rounded-md bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 text-sm font-medium hover:opacity-90 disabled:opacity-50">Use this</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Footer: the decision, with its effect stated */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-gray-800">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {primaryCanonical
            ? <><Dup /> will be archived into <Canon />; {plural(newSources.length, 'source')} attached, nothing changed on the primary record.</>
            : <><Dup /> will be archived; on <Canon />: {plural(totals.fields, 'field')} set as chosen, {plural(totals.victims, 'victim')} copied, {plural(totals.perps, 'perpetrator')} linked, {plural(totals.blocks, 'violation block')} brought over, {plural(totals.projects, 'project')} copied, {plural(newSources.length, 'source')} attached.</>}
          {' '}{manual ? 'Not related records the decision and nothing else.' : 'Not a duplicate sends it on to AI validation.'} Un-merge takes all of it back, unless a field is edited again afterwards.
        </p>
        {canDecide && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              disabled={deciding}
              onClick={() => onDecide('DISMISSED', { dismissReason: 'NOT_RELATED' })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <XMarkIcon className="h-4 w-4" /> {manual ? 'Not related' : 'Not a duplicate'}
            </button>
            <button
              type="button"
              disabled={deciding || loading || !!failed || (restrictionMismatch && !ackRestriction)}
              title={restrictionMismatch && !ackRestriction ? 'Acknowledge the usage-restriction caution first' : undefined}
              onClick={() => onDecide('CONFIRMED', buildExtra())}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" /> {deciding ? 'Working…' : (mode === 'merge' ? 'Merge' : 'Confirm duplicate')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
